import React, { useState, useEffect, useRef } from "react";
import { Card, Badge, Row, Col, Spinner, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMusic,
  faMicrophone,
  faClock,
  faSpinner,
  faPlay,
  faQuoteLeft,
  faLanguage,
  faListOl,
} from "@fortawesome/free-solid-svg-icons";
import { useAudioWithTime } from "../context/AudioContext";
import "./components.css";

const LyricsVisualization = ({ project }) => {
  const { currentTime, duration, isPlaying, currentProjectId } = useAudioWithTime();

  const [lyricsData, setLyricsData] = useState(null);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [lyricsError, setLyricsError] = useState(null);
  const [metadata, setMetadata] = useState(null);

  // Get lyrics asset from project
  const lyricsAsset = project?.audio_assets?.find(
    (asset) => asset.type === "LYRICS_AI" && asset.status === "READY"
  );

  // Helper function to determine if data is a URL or direct data
  const isDataUrl = (data) => {
    return (
      typeof data === "string" &&
      (data.startsWith("http") || data.startsWith("/"))
    );
  };

  // Fetch lyrics data from URL or use direct data
  const processLyricsData = async (lyricsData) => {
    if (!lyricsData) return;

    setLyricsLoading(true);
    setLyricsError(null);

    try {
      let data;
      if (isDataUrl(lyricsData)) {
        // Data is a URL, fetch it
        const response = await fetch(lyricsData);
        if (!response.ok) {
          throw new Error(`Failed to fetch lyrics data: ${response.status}`);
        }
        data = await response.json();
      } else {
        // Data is already an object/array, use it directly
        data = lyricsData;
      }

      // Validate data structure - expecting array of segments
      if (Array.isArray(data)) {
        // Ensure each segment has required properties
        const validSegments = data.filter(
          (segment) => 
            segment && 
            typeof segment.start === "number" && 
            !isNaN(segment.start) &&
            typeof segment.end === "number" && 
            !isNaN(segment.end) &&
            segment.text &&
            segment.words && 
            Array.isArray(segment.words)
        );
        setLyricsData(validSegments);
        console.log(
          `Processed ${validSegments.length} valid lyrics segments out of ${data.length} total`
        );
      } else {
        console.warn("Lyrics data is not an array:", data);
        setLyricsError("Invalid lyrics data format - expected array");
      }
    } catch (error) {
      console.error("Error processing lyrics data:", error);
      setLyricsError(error.message);
    } finally {
      setLyricsLoading(false);
    }
  };

  // Process data when component mounts or asset changes
  useEffect(() => {
    if (lyricsAsset) {
      // Extract metadata
      setMetadata(lyricsAsset.metadata);

      // Process lyrics data
      if (lyricsAsset.lyrics) {
        processLyricsData(lyricsAsset.lyrics);
      }
    } else {
      // Reset state when no asset
      setLyricsData(null);
      setMetadata(null);
    }
  }, [lyricsAsset]);

  // Get current segment
  const getCurrentSegment = () => {
    if (
      !lyricsData ||
      !Array.isArray(lyricsData) ||
      currentTime === undefined ||
      currentTime === null
    )
      return null;

    // Only highlight if this is the current project playing
    if (String(currentProjectId) !== String(project?.id)) return null;

    return lyricsData.find((segment) => {
      return currentTime >= segment.start && currentTime <= segment.end;
    });
  };

  // Get current word within the current segment
  const getCurrentWord = () => {
    const currentSegment = getCurrentSegment();
    if (!currentSegment || !currentSegment.words) return null;

    return currentSegment.words.find((word) => {
      return currentTime >= word.start && currentTime <= word.end;
    });
  };

  const currentSegment = getCurrentSegment();
  const currentWord = getCurrentWord();

  // Format time display
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Render metadata section
  const renderMetadata = () => {
    if (!metadata) return null;

    const languageCount = lyricsData ? 
      [...new Set(lyricsData.map(segment => segment.language))].length : 0;
    const totalWords = lyricsData ? 
      lyricsData.reduce((total, segment) => total + (segment.words?.length || 0), 0) : 0;

    return (
      <Card
        className="mb-3"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.02)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Card.Body className="py-3">
          <h6 className="mb-3" style={{ color: "#d9d9d9" }}>
            <FontAwesomeIcon
              icon={faMicrophone}
              className="me-2"
              style={{ color: "var(--purple-accent)" }}
            />
            Lyrics Analysis Metadata
          </h6>
          
          <Row className="g-2">
            {languageCount > 0 && (
              <Col sm={6} md={3}>
                <div className="text-center p-2" style={{ 
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.1)"
                }}>
                  <FontAwesomeIcon icon={faLanguage} style={{ color: "var(--purple-accent)", fontSize: "var(--font-lg)" }} className="mb-1" />
                  <div style={{ color: "#d9d9d9", fontSize: "var(--font-base)", fontWeight: "500" }}>
                    {languageCount} Language{languageCount > 1 ? 's' : ''}
                  </div>
                </div>
              </Col>
            )}

            {totalWords > 0 && (
              <Col sm={6} md={3}>
                <div className="text-center p-2" style={{ 
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.1)"
                }}>
                  <FontAwesomeIcon icon={faListOl} style={{ color: "var(--purple-accent)", fontSize: "var(--font-lg)" }} className="mb-1" />
                  <div style={{ color: "#d9d9d9", fontSize: "var(--font-base)", fontWeight: "500" }}>
                    {totalWords} Words
                  </div>
                </div>
              </Col>
            )}

            {lyricsData && lyricsData.length > 0 && (
              <Col sm={6} md={3}>
                <div className="text-center p-2" style={{ 
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.1)"
                }}>
                  <FontAwesomeIcon icon={faQuoteLeft} style={{ color: "var(--purple-accent)", fontSize: "var(--font-lg)" }} className="mb-1" />
                  <div style={{ color: "#d9d9d9", fontSize: "var(--font-base)", fontWeight: "500" }}>
                    {lyricsData.length} Segments
                  </div>
                </div>
              </Col>
            )}

            {lyricsData && lyricsData.length > 0 && (
              <Col sm={6} md={3}>
                <div className="text-center p-2" style={{ 
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.1)"
                }}>
                  <FontAwesomeIcon icon={faClock} style={{ color: "var(--purple-accent)", fontSize: "var(--font-lg)" }} className="mb-1" />
                  <div style={{ color: "#d9d9d9", fontSize: "var(--font-base)", fontWeight: "500" }}>
                    {formatTime(lyricsData[lyricsData.length - 1]?.end || 0)}
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // Render lyrics display
  const renderLyricsDisplay = () => {
    if (lyricsLoading) {
      return (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <p className="small text-muted mt-2">Loading lyrics...</p>
        </div>
      );
    }

    if (lyricsError) {
      return (
        <Alert variant="warning" className="small">
          {lyricsError}
        </Alert>
      );
    }

    if (!lyricsData || !Array.isArray(lyricsData) || lyricsData.length === 0) {
      return (
        <div className="text-center py-3">
          <FontAwesomeIcon icon={faMicrophone} className="text-muted mb-2" />
          <p className="small text-muted">No lyrics data available</p>
        </div>
      );
    }

    // Check if this project is currently playing
    const isCurrentProject = String(currentProjectId) === String(project?.id);

    return (
      <div className="lyrics-display">
        <h6 className="mb-3" style={{ color: "#d9d9d9" }}>
          <FontAwesomeIcon icon={faQuoteLeft} className="me-2" />
          Lyrics Timeline
        </h6>

        <div 
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "8px",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            padding: "16px"
          }}
        >
          {lyricsData.map((segment, segmentIndex) => {
            const isCurrentSegment = currentSegment?.start === segment.start;
            const isPastSegment = isCurrentProject && currentTime > segment.end;
            const isFutureSegment = isCurrentProject && currentTime < segment.start;

            return (
              <div
                key={segmentIndex}
                className="lyrics-segment mb-3 p-3"
                style={{
                  borderRadius: "8px",
                  border: isCurrentSegment 
                    ? "2px solid var(--purple-accent)" 
                    : "1px solid rgba(255, 255, 255, 0.1)",
                  backgroundColor: isCurrentSegment 
                    ? "rgba(179, 95, 208, 0.15)" 
                    : isPastSegment 
                    ? "rgba(144, 238, 144, 0.1)" 
                    : "rgba(255, 255, 255, 0.02)",
                  transition: "all 0.3s ease"
                }}
              >
                {/* Segment header with timing */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="segment-timing">
                    <Badge 
                      bg="dark" 
                      style={{ 
                        fontSize: "var(--font-sm)",
                        backgroundColor: isCurrentSegment ? "var(--purple-accent)" : "#495057"
                      }}
                    >
                      {formatTime(segment.start)} - {formatTime(segment.end)}
                    </Badge>
                    {segment.language && (
                      <Badge bg="secondary" className="ms-2" style={{ fontSize: "var(--font-xs)" }}>
                        {segment.language}
                      </Badge>
                    )}
                  </div>
                  {isCurrentSegment && (
                    <FontAwesomeIcon 
                      icon={faPlay} 
                      style={{ color: "var(--purple-accent)", fontSize: "var(--font-sm)" }} 
                    />
                  )}
                </div>

                {/* Segment text with word-level highlighting */}
                <div className="segment-text" style={{ lineHeight: "1.6" }}>
                  {segment.words.map((word, wordIndex) => {
                    const isCurrentWord = isCurrentProject && 
                      currentWord?.start === word.start && 
                      currentWord?.end === word.end;
                    const isPastWord = isCurrentProject && currentTime > word.end;
                    const isFutureWord = isCurrentProject && currentTime < word.start;

                    return (
                      <span
                        key={wordIndex}
                        className="lyrics-word"
                        style={{
                          color: isCurrentWord 
                            ? "#ffffff" 
                            : isPastWord 
                            ? "rgba(144, 238, 144, 0.8)" 
                            : isFutureWord 
                            ? "rgba(153, 153, 153, 0.6)" 
                            : "#d9d9d9",
                          backgroundColor: isCurrentWord 
                            ? "var(--purple-accent)" 
                            : "transparent",
                          padding: isCurrentWord ? "2px 4px" : "0",
                          borderRadius: isCurrentWord ? "4px" : "0",
                          fontWeight: isCurrentWord ? "600" : "normal",
                          transition: "all 0.2s ease",
                          marginRight: "4px",
                          display: "inline-block"
                        }}
                        title={`${word.word} (${formatTime(word.start)} - ${formatTime(word.end)})`}
                      >
                        {word.word}
                        {word.syllables && word.syllables.length > 1 && (
                          <span className="syllable-indicator" style={{ fontSize: "var(--font-sm)", opacity: 0.5 }}>
                            •
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>

                {/* Show confidence score if available */}
                {segment.words.some(word => word.score !== undefined) && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Avg. confidence: {
                        Math.round(
                          segment.words.reduce((sum, word) => sum + (word.score || 0), 0) / 
                          segment.words.length * 100
                        )
                      }%
                    </small>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary info */}
        <div className="text-center mt-3">
          <small className="text-muted">
            Total segments: {lyricsData.length} | Duration:{" "}
            {formatTime(lyricsData[lyricsData.length - 1]?.end || 0)}
            {isCurrentProject && currentTime !== undefined && (
              <span> | Current position: {formatTime(currentTime)}</span>
            )}
          </small>
        </div>
      </div>
    );
  };

  // Show loading state while fetching data
  if (!lyricsAsset) {
    return (
      <div className="text-center py-4">
        <FontAwesomeIcon
          icon={faMicrophone}
          className="text-muted mb-2"
          style={{ fontSize: "var(--font-3xl)", opacity: 0.3 }}
        />
        <p className="text-muted">
          No lyrics data available for this project
        </p>
      </div>
    );
  }

  if (lyricsLoading && !lyricsData) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="text-muted mt-2">Loading lyrics data...</p>
      </div>
    );
  }

  // Check if we have valid data to show
  const hasValidData = lyricsData && lyricsData.length > 0;

  if (!hasValidData && !lyricsLoading) {
    return (
      <div className="text-center py-4">
        <FontAwesomeIcon
          icon={faMicrophone}
          className="text-muted mb-2"
          style={{ fontSize: "var(--font-3xl)", opacity: 0.3 }}
        />
        <p className="text-muted">No valid lyrics data found</p>
      </div>
    );
  }

  const isCurrentProject = String(currentProjectId) === String(project?.id);

  return (
    <div className="lyrics-visualization">
      {/* Metadata */}
      {renderMetadata()}

      {/* Legend */}
      <div className="mb-3 p-3" style={{
        backgroundColor: "rgba(255, 255, 255, 0.02)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: "8px"
      }}>
        <h6 className="mb-2" style={{ color: "#d9d9d9", fontSize: "var(--font-base)" }}>Legend:</h6>
        <div className="d-flex flex-wrap gap-3">
          <div className="d-flex align-items-center gap-1">
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: "var(--purple-accent)",
                borderRadius: "3px"
              }}
            ></div>
            <small className="text-muted">Currently Playing</small>
          </div>
          <div className="d-flex align-items-center gap-1">
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: "rgba(144, 238, 144, 0.6)",
                borderRadius: "3px"
              }}
            ></div>
            <small className="text-muted">Already Played</small>
          </div>
          <div className="d-flex align-items-center gap-1">
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                borderRadius: "3px"
              }}
            ></div>
            <small className="text-muted">Upcoming / Not Playing</small>
          </div>
        </div>
      </div>

      {/* Status message when not playing */}
      {!isCurrentProject && lyricsData && (
        <div className="text-center py-2 mb-3">
          <small className="text-muted">
            <FontAwesomeIcon icon={faMusic} className="me-2" />
            Play an audio file from this project to see real-time lyrics highlighting
          </small>
        </div>
      )}

      {/* Lyrics display */}
      {renderLyricsDisplay()}
    </div>
  );
};

export default LyricsVisualization; 